import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today } from '../../hooks/useApi';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Can from '../../components/Can';
import './MeterReading.css';

/**
 * Part 14 update: admin can EDIT a saved meter reading.
 * Click "Edit" on an existing reading → form becomes editable, Save sends PUT.
 * Backend handles tank stock reversal + re-application atomically.
 */
export default function MeterReading({ entryDate }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canApprove =
    user?.permissions === '*' ||
    user?.permissions?.meter_readings?.approve;

  // Inside Daily Entry the reading date is fixed to the chosen entry date.
  const [date, setDate] = useState(entryDate || today());
  const shift = 1; // Force Shift 1
  const [rows, setRows] = useState([]);
  const [alreadyExists, setAlreadyExists] = useState(false);
  const [existingId, setExistingId] = useState(null);
  const [editMode, setEditMode] = useState(false);   // NEW — admin edit state
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const [startDate, setStartDate] = useState(null);
  const [editingStartDate, setEditingStartDate] = useState(false);
  const [tempStartDate, setTempStartDate] = useState('');

  const notify = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4500); };

  useEffect(() => {
    api.meterReadings.getStartDate()
      .then(res => {
        setStartDate(res.meter_start_date || null);
        setTempStartDate(res.meter_start_date || '');
      })
      .catch(err => console.error("Error fetching start date:", err));
  }, []);

  const handleSaveStartDate = async () => {
    try {
      await api.meterReadings.updateStartDate(tempStartDate);
      setStartDate(tempStartDate || null);
      setEditingStartDate(false);
      notify('success', t('meterReading.toasts.startDateUpdated'));
      reload();
    } catch (e) {
      notify('error', t('meterReading.toasts.startDateFailed', { msg: e.message }));
    }
  };

  const { data, loading, error, reload } = useApi(
    () => api.meterReadings.template(date, shift),
    [date, shift]
  );

  useEffect(() => {
    if (!data) return;
    setAlreadyExists(!!data.already_exists);
    setExistingId(data.reading_id || null);
    setEditMode(false);   // reset edit mode on data reload

    if (data.already_exists) {
      setRows((data.rows || []).map(r => ({
        item_id: r.id,                       // (Part 15) needed for ?action=file&item_id=
        file_path: r.file_path || null,        // (Part 15) drives the 📎 button
        nozzle_id: r.nozzle_id,
        nozzle_no: r.nozzle_no,
        pump_no: r.pump_no,
        tank_id: r.tank_id,
        tank_no: r.tank_no,
        tank_stock: Number(r.tank_stock || 0),
        purchase_qty: Number(r.purchase_qty || 0),
        product_code: r.product_code || r.nozzle_product,
        product_name: r.product_name,
        open_read: Number(r.open_read),
        close_read: Number(r.close_read),
        testing: Number(r.testing || 0),
        sale_qty: Number(r.sale_qty),
        product_rate: Number(r.item_rate || 0),
        tax_per: Number(r.tax_per || 0),
        amount: Number(r.amount || 0),
        readOnly: true,
      })));
    } else {
      setRows((data.rows || []).map(r => ({
        ...r,
        item_id: null,
        file_path: null,
        tank_stock: Number(r.tank_stock || 0),
        purchase_qty: Number(r.purchase_qty || 0),
        readOnly: false
      })));
    }
  }, [data]);

  const updateRow = (idx, field, value) => {
    setRows(prev => {
      const next = [...prev];
      const r = { ...next[idx] };
      const num = value === '' ? '' : parseFloat(value);
      r[field] = isNaN(num) ? value : num;
      next[idx] = r;
      return next;
    });
  };

  /* ═════════════ validation & tank aggregation ═════════════ */
  const tankAggregates = useMemo(() => {
    const agg = {};
    rows.forEach(r => {
      const open = Number(r.open_read) || 0;
      const close = r.close_read === '' ? null : Number(r.close_read);
      const sale = close !== null ? +(close - open).toFixed(3) : 0;
      if (!agg[r.tank_id]) {
        agg[r.tank_id] = {
          tank_no: r.tank_no,
          opening_stock: r.tank_stock,
          purchase: r.purchase_qty,
          sale_qty: 0
        };
      }
      agg[r.tank_id].sale_qty += sale;
    });
    // Calculate closing stock for each tank
    Object.keys(agg).forEach(tid => {
      agg[tid].closing_stock = +(agg[tid].opening_stock + agg[tid].purchase - agg[tid].sale_qty).toFixed(3);
    });
    return agg;
  }, [rows]);

  const rowsWithValidation = useMemo(() => rows.map(r => {
    const open = Number(r.open_read) || 0;
    const closeStr = String(r.close_read || '').trim();
    const close = closeStr === '' ? null : Number(closeStr);
    const testing = Math.max(0, Number(r.testing) || 0);

    // The meter measures ALL fuel that passed through, INCLUDING testing draws.
    //   Total Dispensed = Closing − Opening
    //   Sale (L)        = Total Dispensed − Testing   (testing is not revenue)
    const dispensed = (close !== null) ? +(close - open).toFixed(3) : 0;
    const sale = (close !== null) ? +(dispensed - testing).toFixed(3) : 0;

    const rate = Number(r.product_rate) || 0;
    const tax = Number(r.tax_per) || 0;
    const amountBeforeTax = sale > 0 ? sale * rate : 0;
    const taxAmt = +(amountBeforeTax * tax / 100).toFixed(2);
    const amount = +(amountBeforeTax + taxAmt).toFixed(2);

    const tankAgg = tankAggregates[r.tank_id] || {};
    const clStock = tankAgg.closing_stock || 0;

    const errors = [];
    const warnings = [];
    if (close !== null && close < open) errors.push(t('meterReading.errors.closingLtOpening'));
    if (testing > dispensed) errors.push(t('meterReading.errors.testingExceedsDispensed'));
    if (sale < 0) errors.push(t('meterReading.errors.saleNegative'));
    if (clStock < 0) warnings.push(t('meterReading.warnings.tankNegative', { tank: r.tank_no, stock: clStock.toFixed(1) }));

    return { ...r, sale_qty: sale, testing, amount, tax_amt: taxAmt, errors, warnings, cl_stock: clStock };
  }), [rows, tankAggregates]);

  const hasErrors = rowsWithValidation.some(r => r.errors.length > 0);
  const hasEntries = rowsWithValidation.some(r => {
    const closeStr = String(r.close_read || '').trim();
    return closeStr !== '' && Number(closeStr) >= 0;
  });

  const productTotals = useMemo(() => {
    const map = {};
    rowsWithValidation.forEach(r => {
      if (!r.product_code) return;
      if (!map[r.product_code]) {
        map[r.product_code] = { name: r.product_name, qty: 0, amount: 0, rate: r.product_rate };
      }
      map[r.product_code].qty += (r.sale_qty || 0);
      map[r.product_code].amount += (r.amount || 0);
    });
    return map;
  }, [rowsWithValidation]);

  const grandTotalQty = Object.values(productTotals).reduce((s, p) => s + p.qty, 0);
  const grandTotalAmount = Object.values(productTotals).reduce((s, p) => s + p.amount, 0);

  /* ═════════════ save (create or update) ═════════════ */
  const handleSave = async () => {
    if (hasErrors) { notify('error', t('meterReading.toasts.fixErrors')); return; }
    if (!hasEntries) { notify('error', t('meterReading.toasts.enterClosing')); return; }

    setSaving(true);
    try {
      const items = rowsWithValidation.map(r => ({
        nozzle_id: r.nozzle_id,
        open_read: Number(r.open_read) || 0,
        close_read: Number(r.close_read) || 0,
        testing: Math.max(0, Number(r.testing) || 0),
        product_rate: Number(r.product_rate) || 0,
        tax_per: Number(r.tax_per) || 0,
      }));

      if (editMode && existingId) {
        await api.meterReadings.update(existingId, { items });
        notify('success', t('meterReading.toasts.readingUpdated'));
      } else {
        const res = await api.meterReadings.create({
          reading_date: date, shift_no: shift, items,
        });
        notify('success', t('meterReading.toasts.readingSaved', { qty: grandTotalQty.toFixed(2), amount: grandTotalAmount.toFixed(2) }));
      }
      setEditMode(false);
      reload();
    } catch (e) {
      notify('error', '❌ ' + e.message);
    }
    setSaving(false);
  };

  const handleStartEdit = () => {
    // Unlock row inputs for editing
    setRows(prev => prev.map(r => ({ ...r, readOnly: false })));
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    reload();  // reset to server state
  };

  const handleDeleteReading = async () => {
    if (!existingId) return;
    if (!window.confirm(t('meterReading.confirmReset'))) return;

    setSaving(true);
    try {
      await api.meterReadings.remove(existingId);
      notify('success', t('meterReading.toasts.readingsReset'));
      reload();
    } catch (e) {
      notify('error', '❌ ' + e.message);
    }
    setSaving(false);
  };

  // (Part 15) Download an item's photo and open it in a new tab.
  // Mirrors the Purchase.jsx handleViewAttachment pattern exactly.
  const handleViewPhoto = async (itemId) => {
    if (!itemId) return;
    try {
      const blob = await api.meterReadings.downloadItemAttachment(itemId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      notify('error', t('meterReading.toasts.photoLoadFailed', { msg: e?.message || t('meterReading.unknownError') }));
    }
  };

  return (
    <div className="mr-page">
      <div className="page-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h2 className="page-title" style={{ margin: 0 }}>{t('meterReading.title')}</h2>
          {!canApprove && !alreadyExists && (
            <div className="mr-role-note" style={{ margin: 0 }}>
              🔒 {t('meterReading.roleNote')}
            </div>
          )}
        </div>

        {/* Start Date Pill on the right */}
        {editingStartDate ? (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            background: '#edf2f7',
            border: '1px solid #cbd5e0',
            borderRadius: '20px',
            padding: '4px 12px',
            fontSize: '13px',
            color: '#4a5568',
            gap: 6
          }}>
            🏁 {t('meterReading.startDate')}:
            <input
              type="date"
              value={tempStartDate}
              onChange={e => setTempStartDate(e.target.value)}
              style={{
                border: 'none',
                background: 'transparent',
                outline: 'none',
                fontSize: '12px',
                color: '#4a5568',
                padding: 0,
                margin: 0,
              }}
            />
            <button onClick={handleSaveStartDate} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontSize: '13px' }} title={t('common.save')}>💾</button>
            <button onClick={() => { setEditingStartDate(false); setTempStartDate(startDate || ''); }} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontSize: '13px' }} title={t('common.cancel')}>✕</button>
          </div>
        ) : (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            background: '#edf2f7',
            border: '1px solid #e2e8f0',
            borderRadius: '20px',
            padding: '4px 12px',
            fontSize: '13px',
            color: '#4a5568',
            gap: 6
          }}>
            🏁 {t('meterReading.startDate')}: {startDate ? new Date(startDate).toLocaleDateString('en-GB') : '—'}
            {canApprove && (
              <button
                onClick={() => setEditingStartDate(true)}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  padding: 0,
                  marginLeft: 4,
                }}
                title={t('meterReading.editStartDate')}
              >
                ✏️
              </button>
            )}
          </div>
        )}
      </div>

      {msg && <div className={`mr-toast mr-toast-${msg.type}`}>{msg.text}</div>}

      <div className="mr-filters section-card">
        <div className="form-group">
          <label>{t('meterReading.readingDate')}</label>
          <input type="date" value={date} disabled={editMode}
            onChange={e => setDate(e.target.value)} />
        </div>
        <div className="form-group" style={{ opacity: 0.6 }}>
          <label>{t('meterReading.shiftConsolidated')}</label>
          <div className="mr-static-val">{t('meterReading.shift1AllDay')}</div>
        </div>
        <div className="mr-filter-right">
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {editMode ? t('meterReading.editingSavedReading') :
              alreadyExists ? t('meterReading.readingAlreadySaved') :
                t('meterReading.nozzleCount', { count: rows.length })}
          </span>
        </div>
      </div>

      {error && <ErrBanner msg={error} />}
      {data?.message && <div className="mr-empty-note">⚠️ {data.message}</div>}

      {loading ? <Spinner /> : (
        <>
          {/* Main Nozzle Entries Table */}
          <div className="section-card" style={{ marginBottom: 20 }}>
            <div className="section-header">
              {editMode ? t('meterReading.editReadingsWithDate', { date }) :
                alreadyExists ? t('meterReading.savedReadingsWithDate', { date }) :
                  t('meterReading.nozzleEntriesWithDate', { date })}

              {alreadyExists && canApprove && !editMode && (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  <button className="btn btn-sm" onClick={handleStartEdit} style={{ background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8' }}>
                    ✏️ {t('meterReading.editReading')}
                  </button>
                  <button className="btn btn-sm" onClick={handleDeleteReading} style={{ background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2' }}>
                    🗑️ {t('meterReading.resetReading')}
                  </button>
                </div>
              )}
            </div>

            <div className="table-wrapper">
              <table className="mr-table">
                <thead>
                  <tr>
                    <th>{t('common.pump')}</th>
                    <th>{t('common.nozzle')}</th>
                    <th>{t('common.tank')}</th>
                    <th>{t('common.product')}</th>
                    <th>{t('meterReading.openingMtr')}</th>
                    <th>{t('meterReading.closingMtr')}</th>
                    <th>{t('meterReading.testingL')}</th>
                    <th>{t('meterReading.saleL')}</th>
                    <th>{t('meterReading.rateRs')}</th>
                    <th>{t('meterReading.amountRs')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsWithValidation.length === 0 ? (
                    <tr><td colSpan="10" style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
                      {t('meterReading.noNozzles')}
                    </td></tr>
                  ) : rowsWithValidation.map((r, idx) => (
                    <tr key={r.nozzle_id} className={r.errors.length > 0 ? 'mr-row-err' : ''}>
                      <td style={{ fontWeight: 600 }}>{r.pump_no || '—'}</td>
                      <td style={{ fontWeight: 600 }}>
                        {r.nozzle_no || '—'}
                        {/* (Part 15) Nozzle photo indicator. Same paperclip
                            idiom used by Purchases — keeps the page visually
                            consistent. The button is invisible-but-present
                            when there's no photo so column widths stay stable. */}
                        {r.file_path && r.item_id && (
                          <button
                            onClick={() => handleViewPhoto(r.item_id)}
                            title={t('meterReading.viewNozzlePhoto')}
                            style={{
                              marginLeft: 6,
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: 16,
                              padding: 0,
                            }}
                          >📎</button>
                        )}
                      </td>
                      <td><span className="mr-tank-pill">{r.tank_no || '—'}</span></td>
                      <td><span className="mr-product-pill">{r.product_code}</span></td>
                      <td>
                        <input
                          type="number" step="0.001"
                          className={`mr-input ${canApprove ? '' : 'mr-input-locked'}`}
                          value={r.open_read}
                          disabled={r.readOnly || !canApprove}
                          onChange={e => updateRow(idx, 'open_read', e.target.value)} />
                      </td>
                      <td>
                        <input
                          type="number" step="0.001"
                          className="mr-input mr-input-close"
                          value={r.close_read}
                          disabled={r.readOnly}
                          onChange={e => updateRow(idx, 'close_read', e.target.value)}
                          placeholder="0.000" />
                      </td>
                      <td>
                        <input
                          type="number" step="0.001" min="0"
                          className="mr-input"
                          style={{ width: '80px' }}
                          value={r.testing ?? ''}
                          disabled={r.readOnly}
                          onChange={e => updateRow(idx, 'testing', e.target.value)}
                          placeholder="0.000" />
                      </td>
                      <td style={{
                        fontWeight: 700, fontFamily: 'monospace',
                        color: r.sale_qty < 0 ? 'var(--danger)' : 'var(--secondary)',
                      }}>{r.sale_qty.toFixed(3)}</td>
                      <td>
                        <input
                          type="number" step="0.01"
                          className={`mr-input ${canApprove ? '' : 'mr-input-locked'}`}
                          style={{ width: '80px' }}
                          value={r.product_rate}
                          disabled={r.readOnly || !canApprove}
                          onChange={e => updateRow(idx, 'product_rate', e.target.value)}
                        />
                      </td>
                      <td style={{ fontWeight: 700, fontFamily: 'monospace', color: 'var(--primary)', textAlign: 'right' }}>
                        {r.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                  {rowsWithValidation.length > 0 && (
                    <tr className="mr-grand-total">
                      <td colSpan="7" style={{ textAlign: 'right' }}>{t('meterReading.grandTotal')}:</td>
                      <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>{grandTotalQty.toFixed(3)} L</td>
                      <td></td>
                      <td style={{ fontWeight: 700, fontFamily: 'monospace', color: 'var(--primary)', textAlign: 'right' }}>
                        ₹ {grandTotalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  {rowsWithValidation.filter(r => r.errors.length > 0 || r.warnings.length > 0).map((r) => (
                    <tr key={`err-${r.nozzle_id}`} className="mr-errrow">
                      <td colSpan="10">
                        {r.errors.length > 0 && (
                          <div style={{ color: 'var(--danger)', fontWeight: 600 }}>
                            ❌ <strong>{r.nozzle_no}:</strong> {r.errors.join(' · ')}
                          </div>
                        )}
                        {r.warnings.length > 0 && (
                          <div style={{ color: '#d69e2e', fontWeight: 600, marginTop: r.errors.length > 0 ? 4 : 0 }}>
                            ⚠️ <strong>{r.nozzle_no}:</strong> {r.warnings.join(' · ')}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tfoot>
              </table>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '20px' }}>
            {/* Tank Summary Table (Aggregated) */}
            <div className="section-card">
              <div className="section-header">{t('meterReading.tankStockImpact')}</div>
              <table className="mr-table">
                <thead>
                  <tr>
                    <th>{t('common.tank')}</th>
                    <th style={{ textAlign: 'right' }}>{t('meterReading.opStock')}</th>
                    <th style={{ textAlign: 'right' }}>{t('meterReading.purchase')}</th>
                    <th style={{ textAlign: 'right' }}>{t('meterReading.totalDispensed')}</th>
                    <th style={{ textAlign: 'right' }}>{t('meterReading.estClosing')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(tankAggregates).map(([tid, agg]) => (
                    <tr key={tid}>
                      <td style={{ fontWeight: 600 }}>{agg.tank_no}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{agg.opening_stock.toFixed(2)}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'monospace', color: 'var(--secondary)' }}>+{agg.purchase.toFixed(2)}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'monospace', color: agg.sale_qty > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
                        {agg.sale_qty > 0 ? `−${agg.sale_qty.toFixed(2)}` : '0.00'}
                      </td>
                      <td style={{
                        textAlign: 'right', fontFamily: 'monospace', fontWeight: 700,
                        color: agg.closing_stock < 0 ? 'var(--danger)' : 'var(--primary)'
                      }}>
                        {agg.closing_stock.toFixed(2)} L
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10 }}>
                💡 {t('meterReading.closingStockHint')}
              </div>
            </div>

            {/* Product Summary */}
            <div className="section-card">
              <div className="section-header">{t('meterReading.productWiseSales')}</div>
              <div className="mr-summary-grid" style={{ gridTemplateColumns: '1fr' }}>
                {Object.entries(productTotals).map(([code, v]) => (
                  <div key={code} className="mr-summary-tile" style={{ marginBottom: 8 }}>
                    <div className="mr-summary-label">{v.name || code}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span className="mr-summary-qty">{v.qty.toFixed(3)} L</span>
                      <span className="mr-summary-amt">₹ {v.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Final Action Bar */}
          {(editMode || !alreadyExists) && rows.length > 0 && (
            <div className="mr-actions" style={{ marginTop: 20 }}>
              {editMode && (
                <button className="btn" onClick={handleCancelEdit} disabled={saving}>
                  {t('common.cancel')}
                </button>
              )}
              <Can module="meter_readings" action={editMode ? 'update' : 'create'} fallback={
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                  🔒 {editMode ? t('meterReading.cannotEditReadings') : t('meterReading.cannotCreateReadings')}
                </span>
              }>
                <button className="btn btn-primary" disabled={saving || hasErrors || !hasEntries}
                  onClick={handleSave} style={{ minWidth: 200 }}>
                  {saving ? t('meterReading.savingBtn') :
                    editMode ? t('meterReading.updateReading') : t('meterReading.saveReading')}
                </button>
              </Can>
            </div>
          )}
        </>
      )}
    </div>
  );
}
